import { useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import JoinsQueryBuilder from './JoinsQueryBuilder'
import { ReportingContext } from '../../../../../wrappers/ReportingContext'
import { transformQuery } from 'react-querybuilder'
import {
    convertReactSelectObjectToValue,
    getTableNameWithIdentifier,
    validateUniqueness,
} from '../../../../../../utils/reporting/config'
import {
    noBracketsRegex,
    ruleValidation,
} from '../../../../../../utils/reporting/reportValidations'
import { joinRuleProcessor } from '../../../../../../utils/reporting/ruleConfig'
import { joinTypeOptions } from '../../../../select/StaticSelectObjects'
import ReportSelect, { BaseSelect } from '../../../../select/ReportSelect'
import { InputFieldColumnSimple, InputFieldColumn } from '../InputField'

const defaultQuery = {
    not: false,
    rules: [
        {
            field: 'fieldData',
            value: '',
            operator: 'join',
        },
    ],
}

const JoinColumns = [
    {
        Header: () => (
            <span>
                Join Type<span style={{ color: 'red' }}> *</span>
            </span>
        ),
        accessor: 'joinType',
        Cell: ({
            cell,
            value: initialValue,
            row: { original, index },
            column: { id, accessor },
        }) => {
            return (
                <InputFieldColumn
                    name={`joins[${index}].joinType`}
                    isClearable={false}
                >
                    <ReportSelect
                        name={`joins[${index}].joinType`}
                        rules={{ required: 'Required field' }}
                        options={joinTypeOptions}
                        isClearable={false}
                        value={
                            convertReactSelectObjectToValue(initialValue) ||
                            joinTypeOptions[0]
                        }
                    />
                </InputFieldColumn>
            )
        },
    },
    {
        Header: () => (
            <span>
                From Source<span style={{ color: 'red' }}> *</span>
            </span>
        ),
        accessor: 'table1',
        Cell: ({
            cell,
            value: initialValue,
            row: { index },
            column: { id, accessor },
        }) => {
            const { control, errors } = useFormContext()
            const { reportSelectOptions } = useContext(ReportingContext)
            const selectedDataSources = useMemo(() => {
                return reportSelectOptions &&
                    reportSelectOptions.selectedDataSources
                    ? reportSelectOptions.selectedDataSources.map((x) => ({
                          ...x,
                          value: x.label || x.value,
                      }))
                    : []
            }, [reportSelectOptions])

            return (
                <div>
                    <ReportSelect
                        name={`joins[${index}].table1`}
                        control={control}
                        rules={{ required: 'Required field' }}
                        options={selectedDataSources}
                        value={convertReactSelectObjectToValue(initialValue)}
                        errors={errors}
                    />
                </div>
            )
        },
    },
    {
        Header: () => (
            <span>
                To Source<span style={{ color: 'red' }}> *</span>
            </span>
        ),
        accessor: 'table2',
        Cell: ({
            cell,
            value: initialValue,
            row: { index },
            column: { id, accessor },
        }) => {
            const { control, errors } = useFormContext()
            const { reportSelectOptions } = useContext(ReportingContext)
            const options = reportSelectOptions?.dataSources || []

            return (
                <ReportSelect
                    name={`joins[${index}].table2`}
                    control={control}
                    rules={{ required: 'Required field' }}
                    options={options}
                    value={convertReactSelectObjectToValue(initialValue)}
                    errors={errors}
                />
            )
        },
    },
    {
        Header: 'To Source Alias',
        accessor: 'alias',
        Cell: ({
            cell,
            value: initialValue,
            row: { index },
            column: { id, accessor },
        }) => {
            const { register, watch, triggerValidation } = useFormContext()

            const joins = watch('joins')
            const allAliases = useMemo(() => {
                return joins && Array.isArray(joins) && joins.length
                    ? joins.map((v) => v.alias)
                    : []
            }, [joins])
            const allToSources = useMemo(() => {
                return Array.isArray(joins) && joins.length
                    ? joins.map((v) => v.table2)
                    : []
            }, [joins])

            const currentToSource = convertReactSelectObjectToValue(
                watch(`joins[${index}].table2`)
            )
            const isToSourceDuplicated = useMemo(() => {
                if (
                    currentToSource === null ||
                    currentToSource === undefined ||
                    (typeof currentToSource === 'string' &&
                        currentToSource.trim().length === 0)
                )
                    return false

                return (
                    allToSources.filter(
                        (src) =>
                            convertReactSelectObjectToValue(src) ===
                            currentToSource
                    ).length > 1
                )
            }, [allToSources, currentToSource])

            useEffect(() => {
                triggerValidation(`joins[${index}].alias`) // retriggers validation on To Source change
            }, [currentToSource])

            return (
                <InputFieldColumnSimple
                    name={`joins[${index}].alias`}
                    inputProps={{
                        ...{
                            type: 'text',
                            ref: register({
                                pattern: {
                                    value: noBracketsRegex,
                                    message:
                                        'Bracket characters are not allowed',
                                },
                                required: {
                                    value: isToSourceDuplicated,
                                    message:
                                        'To Sources joined multiple times must be aliased',
                                },
                                validate: (v) =>
                                    validateUniqueness(v, allAliases),
                            }),
                            defaultValue: cell.value,
                        },
                    }}
                />
            )
        },
    },
    {
        Header: 'Filters',
        accessor: 'filters',
        Cell: ({
            cell,
            value: initialValue,
            row: { index },
            column: { id, accessor },
        }) => {
            const { control, watch, triggerValidation, setValue } =
                useFormContext()
            const { reportSelectOptions, reportConfig } =
                useContext(ReportingContext)
            const thisReportConfig =
                reportConfig && reportConfig.joins && reportConfig.joins[index]
            const [query, setQuery] = useState(
                (thisReportConfig && thisReportConfig.filters) || defaultQuery
            )
            const dataSourceColumns = useMemo(
                () => reportSelectOptions?.selectedDataSourceColumns || [],
                [reportSelectOptions]
            )

            const selectedDataSources = useMemo(
                () => reportSelectOptions?.selectedDataSources || [],
                [reportSelectOptions]
            )

            const newQuery = watch(`joins[${index}].filters`)
            const table1Identifier = convertReactSelectObjectToValue(
                watch(`joins[${index}].table1`)
            )

            const table1TableName = getTableNameWithIdentifier(
                table1Identifier,
                selectedDataSources
            )

            const table2 = convertReactSelectObjectToValue(
                watch(`joins[${index}].table2`)
            )

            const validator = useCallback(
                (rule) => ruleValidation(rule, null, null, null, null, true),
                []
            )

            const fields = useMemo(
                () => [
                    {
                        name: `fieldData`,
                        label: `fieldData`,
                        index,
                        selectedDataSources,
                        dataSourceColumns,
                        table1Identifier,
                        table1TableName,
                        table2,
                        validator,
                    },
                ],
                [
                    table1Identifier,
                    table1TableName,
                    table2,
                    selectedDataSources,
                    index,
                    dataSourceColumns,
                    validator,
                ]
            )

            useEffect(() => {
                if (!table1Identifier || !table2) {
                    setValue(`joins[${index}].filters`, defaultQuery)
                }

                const timeout = setTimeout(() => {
                    triggerValidation(`joins[${index}].filters`)
                }, 100)

                return () => clearTimeout(timeout)
            }, [table1Identifier, table2, setValue, triggerValidation, index])

            useEffect(() => {
                if (newQuery) {
                    const transformedQuery = transformQuery(newQuery, {
                        ruleProcessor: (rule) =>
                            joinRuleProcessor(rule, fields),
                    })
                    setQuery(transformedQuery)
                }
            }, [watch, newQuery, fields])

            const validateFilters = (value) => {
                let query
                try {
                    query =
                        typeof value === 'string' ? JSON.parse(value) : value
                } catch {
                    return 'Invalid filter format'
                }

                const hasValidJoinRule = (node, depth = 0) => {
                    if (!node || !Array.isArray(node.rules)) {
                        return false
                    }

                    return node.rules.some((r, i) => {
                        if (r && Array.isArray(r.rules)) {
                            return hasValidJoinRule(r, depth + 1)
                        }

                        if (
                            !r ||
                            r.operator !== 'join' ||
                            typeof r.value !== 'string'
                        ) {
                            return false
                        }

                        const parts = r.value.split(',')
                        if (parts.length !== 2) {
                            return false
                        }

                        const left = parts[0]?.trim()
                        const right = parts[1]?.trim()
                        const isValid = left?.length > 0 && right?.length > 0

                        return isValid
                    })
                }

                return (
                    hasValidJoinRule(query) ||
                    'Join filters are required when both tables are selected (use "left,right").'
                )
            }

            return (
                <Controller
                    as={
                        <JoinsQueryBuilder
                            query={query}
                            fields={fields}
                            operators={['join']}
                        />
                    }
                    name={`joins[${index}].filters`}
                    control={control}
                    onChangeName="onQueryChange"
                    rules={{ validate: validateFilters }}
                    // onChange={onChange}
                    // viewOnly={viewOnly}
                />
            )
        },
    },
]

export default JoinColumns
